import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CirclePlus } from "lucide-react";
import {
  CATALOGS,
  type CatalogDefinition,
  type CatalogKey,
} from '@/features/configuracion/catalogos/config/catalogs.config';
import CatalogFilters from '@/features/configuracion/catalogos/components/CatalogFilters';
import CatalogTable from '@/features/configuracion/catalogos/components/CatalogTable';
import CatalogFormModal, { type FormField } from '@/features/configuracion/catalogos/components/CatalogFormModal';
import { useCatalogList } from '@/features/configuracion/catalogos/hooks/useCatalogList';
import { useCatalogItem } from '@/features/configuracion/catalogos/hooks/useCatalogItem';
import { useUsersList } from '@/features/configuracion/catalogos/hooks/useUsersList';
import type { Area } from '@/features/configuracion/catalogos/types/catalogs.types';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { useAuth } from '@/shared/hooks/useAuth';
import { apiService } from '@/services/api';
import { getAreas } from '@/features/configuracion/api/areaService';
import { useFilaSaliente } from '@/shared/hooks/useFilaSaliente';

function CatalogListView({
  catalogKey,
  showBackButton = true,
  onClickBackButton,
}: {
  catalogKey: CatalogKey;
  showBackButton?: boolean;
  onClickBackButton?: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const def = CATALOGS[catalogKey] as CatalogDefinition<any>;
  const [params, setParams] = useState<{
    q?: string;
    page?: number;
    limit?: number;
    showDeleted?: boolean;
  }>({ page: 1, limit: 10 });

  const { data, total, loading, refetch } = useCatalogList<any>(
    def.apiBase,
    params
  );
  const { create, update, remove, restore } = useCatalogItem<any>(def.apiBase);
  const { users } = useUsersList();

  // Estados para catálogos específicos
  const [areas, setAreas] = useState<Area[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Nuevo estado para encargados filtrados
  const [managers, setManagers] = useState<any[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const [modal, setModal] = useState<{
    mode: "create" | "edit";
    row?: any;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const { claseDeFila, animarSalida } = useFilaSaliente();
  const [deleting, setDeleting] = useState(false);

  const canCreate = userHasPermission(
    `generales.catalogos.${catalogKey}.create`,
    user?.rbac?.effectivePermissions || []
  );
  const canEdit = userHasPermission(
    `generales.catalogos.${catalogKey}.update`,
    user?.rbac?.effectivePermissions || []
  );
  const canDelete = userHasPermission(
    `generales.catalogos.${catalogKey}.delete`,
    user?.rbac?.effectivePermissions || []
  );

  const columns = useMemo(() => def.columns, [def]);

  // Cargar áreas si el catálogo es de puestos
  useEffect(() => {
    if (catalogKey === "puestos") {
      loadAreas();
    }
  }, [catalogKey]);

  //  Cargar encargados si el catálogo es de áreas
  useEffect(() => {
    if (catalogKey === "areas") {
      loadManagers();
    }
  }, [catalogKey]);

  // Refactorizado: Usa el servicio getAreas (sin hardcoding)
  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const areasData = await getAreas(); // El servicio ya maneja token y URL
      // Filtramos áreas activas
      setAreas(areasData.filter((a: Area) => a.activa !== false));
    } catch (error) {
      console.error("Error al cargar áreas:", error);
      setAreas([]);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Carga encargados usando apiService
  const loadManagers = async () => {
    setLoadingManagers(true);
    try {
      // Usamos el método que agregamos a la clase ApiService
      const data = await apiService.getUsers();
      const onlyManagers = Array.isArray(data)
        ? data.filter((u) => u.isAreaManager === true)
        : [];
      setManagers(onlyManagers);
    } catch (error) {
      console.error("Error al cargar encargados:", error);
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  };

  // Configurar los campos del formulario según el catálogo
  const formFields = useMemo<Array<FormField<any>>>(() => {
    switch (catalogKey) {
      case "origenes_candidato":
        return [
          {
            name: "name",
            label: "Nombre",
            type: "text",
            required: true,
            placeholder: "Ej: Bolsa de trabajo, Redes sociales",
            // El mismo numero que valida CreateCandidateSourceDto.
            maxLength: 80,
          },
          {
            name: "visibility",
            label: "Visibilidad",
            type: "select",
            required: true,
            defaultValue: "public",
            options: [
              { value: "public", label: "Público e interno" },
              { value: "internal", label: "Solo interno" },
            ],
            placeholder: "Dónde se puede elegir",
          },
          {
            name: "isReferral",
            label: "Es una recomendación",
            type: "checkbox",
            placeholder: "Al elegirlo, pedir quién recomendó al candidato",
          },
          {
            name: "sortOrder",
            label: "Orden en el desplegable",
            type: "number",
          },
          {
            name: "isActive",
            label: "Disponible",
            type: "checkbox",
            defaultValue: true,
            placeholder: "Se puede elegir al capturar un candidato",
          },
        ];
      case "areas":
        return [
          {
            name: "nombre",
            label: "Nombre",
            type: "text",
            required: true,
            placeholder: "Ej: Desarrollo, Marketing, Ventas",
            maxLength: 100,
          },
          {
            name: "descripcion",
            label: "Descripción",
            type: "textarea",
            placeholder: "Descripción del Departamento/Área",
            // El mismo numero que valida CreateAreaDto.
            maxLength: 1000,
          },
          {
            name: "encargadoId",
            label: "Encargado del Departamento/Área",
            type: "select",
            required: false,
            // Usamos la lista de managers filtrada desde el backend
            options: managers.map((u) => ({
              value: u.id,
              label: `${u.firstName} ${u.lastName} (${u.email})`,
            })),
            placeholder: "Seleccionar encargado (opcional)",
            disabled: loadingManagers,
          },
        ];
      case "puestos":
        return [
          {
            name: "nombre",
            label: "Nombre del Puesto",
            type: "text",
            required: true,
            placeholder: "Ej: Director de Tecnología, Desarrollador Senior",
            maxLength: 100,
          },
          {
            name: "areaId",
            label: "Departamento/Área",
            type: "select",
            required: true,
            options: areas.map((a) => ({
              value: a.id,
              label: a.nombre,
            })),
            placeholder: "Seleccionar Departamento/Área",
            disabled: loadingAreas,
          },
          {
            name: "descripcion",
            label: "Descripción",
            type: "textarea",
            placeholder: "Descripción detallada del puesto",
            // Sin maxLength: la columna es text y el DTO no pone tope. Aqui se
            // escriben descripciones de puesto completas, y el limite ya se
            // quedo corto dos veces.
          },
        ];
      default:
        return [];
    }
  }, [catalogKey, users, areas, loadingAreas, managers, loadingManagers]); // Dependencias actualizadas

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-end">
          {showBackButton && (
            <button
              onClick={() => {
                if (onClickBackButton) {
                  onClickBackButton();
                } else {
                  navigate(-1);
                }
              }}
              className={estiloDeBoton('secundario')}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Volver</span>
            </button>
          )}
          <TituloDePantalla nivel="interna">{`Catálogo de ${def.title}`}</TituloDePantalla>
        </div>
        {canCreate && (
          <button
            className={estiloDeBoton('primario')}
            onClick={() => setModal({ mode: "create" })}
          >
            <CirclePlus className="h-4 w-4" />
            {`Agregar ${def.singleKey == 'area' ? 'área' : def.singleKey}`}
          </button>
        )}
      </div>

      <div className="bg-[#ffffff] border rounded-xl p-4 pt-0 my-6">
        <CatalogFilters
          onChange={(next) => setParams((p) => ({ ...p, page: 1, ...next }))}
        />
      </div>

      <CatalogTable
        rows={data as any}
        columns={columns as any}
        loading={loading}
        canEdit={canEdit}
        canDelete={canDelete}
        claseDeFila={claseDeFila}
        onEdit={(row) => setModal({ mode: "edit", row })}
        onDelete={async (row) => {
          setDeleteTarget(row);
        }}
        onRestore={async (row) => {
          if (!confirm("¿Restaurar elemento?")) return;
          await restore(row.id);
          refetch();
        }}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            disabled={params.page === 1}
            className="px-3 py-1 border rounded-xl disabled:opacity-50"
            onClick={() =>
              setParams((p) => ({
                ...p,
                page: Math.max(1, (p.page ?? 1) - 1),
              }))
            }
          >
            Anterior
          </button>
          <span className="text-sm">Página {params.page}</span>
          <button
            className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() =>
              setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))
            }
            disabled={data.length < (params.limit || 10)}
          >
            Siguiente
          </button>
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            className="ml-2"
            value={String(params.limit)}
            onChange={(valor) =>
              setParams((p) => ({
                ...p,
                page: 1,
                limit: Number(valor),
              }))
            }
            canDelete={false}
            opciones={[10, 20, 50, 100].map((n) => ({ value: String(n), label: `${n}/página` }))}
          />
        </div>
      </div>

      {modal && (
        <CatalogFormModal<any>
          title={`${modal.mode === "create" ? "Crear" : "Editar"} ${def.title}`}
          fields={formFields}
          initial={modal.mode === "edit" ? modal.row : undefined}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            if (modal.mode === "create") await create(payload);
            else await update(modal.row.id, payload);
            setModal(null);
            refetch();
          }}
        />
      )}

      {deleteTarget && (
        <CapaDeModal>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              ¿Eliminar elemento?
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Esta acción no se puede deshacer.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className={estiloDeBoton('secundario')}
                onClick={() => {
                  if (deleting) return;
                  setDeleteTarget(null);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={estiloDeBoton('peligro')}
                disabled={deleting}
                onClick={async () => {
                  try {
                    setDeleting(true);
                    await remove(deleteTarget.id);
                    setDeleteTarget(null);
                    // A-16: la fila sale antes de releer el listado.
                    await animarSalida(deleteTarget.id);
                    refetch();
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
}

export default CatalogListView;
